<template>
  <div
    id="chat-ai"
    class="chat-ai"
    :style="{ '--chat-font-scale': chatFontScale }"
    data-component-id="chat--root"
  >
    <!-- ── Header ─────────────────────────────────────────────────────────── -->
    <div class="chat-ai-header">
      <button
        class="chat-ai-icon-btn chat-ai-history-btn"
        :class="{ 'is-active': showSessionList }"
        @click="toggleSessionList"
        :title="showSessionList ? 'Tutup riwayat (Ctrl+K)' : 'Buka riwayat (Ctrl+K)'"
        aria-label="Riwayat percakapan"
        data-component-id="chat--header--btn-history"
      >
        <svg class="chat-ai-history-svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M3 5h13a4 4 0 0 1 4 4v10a2 2 0 0 0-2-2H3z"></path>
          <path d="M3 5v14"></path>
          <path d="M7 9h9"></path>
          <path d="M7 13h9"></path>
        </svg>
        <span class="chat-ai-history-label">Riwayat</span>
      </button>

      <div class="chat-ai-header-info">
        <div class="chat-ai-title-row">
          <span class="chat-ai-title-dot" aria-hidden="true"></span>
          <input
            v-if="isEditingTitle"
            ref="titleInputEl"
            v-model="titleDraft"
            class="chat-ai-title-input"
            :class="titleSizeClass"
            type="text"
            maxlength="120"
            :placeholder="titleEditPlaceholder"
            @keydown.enter.prevent="commitTitleEdit"
            @keydown.esc.prevent="cancelTitleEdit"
            @blur="commitTitleEdit"
            data-component-id="chat--header--input-title"
          />
          <button
            v-else
            type="button"
            class="chat-ai-title-btn"
            :class="titleSizeClass"
            :title="titleHoverHint"
            :disabled="!canEditTitle"
            @click="startTitleEdit"
            data-component-id="chat--header--btn-title"
          >
            <span class="chat-ai-title-text">{{ sessionTitle || 'Yustian Smart AI' }}</span>
            <span v-if="canEditTitle" class="chat-ai-title-edit-hint" aria-hidden="true">✎</span>
          </button>
        </div>
        <span class="chat-ai-context-label" :title="contextLabel">
          {{ contextLabel }} · {{ sessionDateLabel }}
        </span>
      </div>

      <div class="chat-ai-header-actions">
        <button
          class="chat-ai-icon-btn chat-ai-health-btn"
          :class="healthStatusClass"
          @click="testAiConnection"
          :disabled="healthChecking"
          :title="`${healthLabel} — ${healthTooltip}`"
          :aria-label="`Status koneksi AI: ${healthLabel}`"
          data-component-id="chat--header--btn-health"
        >
          <span class="chat-ai-health-dot" :class="healthStatusClass"></span>
        </button>
        <div class="chat-ai-fontsize-group" role="group" aria-label="Ukuran font chat">
          <button
            class="chat-ai-icon-btn chat-ai-fontsize-btn"
            @click="decreaseChatFont"
            :disabled="chatFontScale <= CHAT_FONT_MIN + 0.001"
            :title="`Perkecil font (${chatFontPercent}%)`"
            aria-label="Perkecil font chat"
            data-component-id="chat--header--btn-font-dec"
          >A−</button>
          <button
            class="chat-ai-icon-btn chat-ai-fontsize-label"
            @click="resetChatFont"
            :title="`Reset font ke 100% (saat ini ${chatFontPercent}%)`"
            aria-label="Reset ukuran font chat"
            data-component-id="chat--header--btn-font-reset"
          >{{ chatFontPercent }}%</button>
          <button
            class="chat-ai-icon-btn chat-ai-fontsize-btn"
            @click="increaseChatFont"
            :disabled="chatFontScale >= CHAT_FONT_MAX - 0.001"
            :title="`Perbesar font (${chatFontPercent}%)`"
            aria-label="Perbesar font chat"
            data-component-id="chat--header--btn-font-inc"
          >A+</button>
        </div>
        <button
          class="chat-ai-icon-btn"
          @click="downloadChat"
          :disabled="!messages.length"
          title="Unduh chat (.md)"
          aria-label="Unduh percakapan"
        >⬇</button>
        <button
          class="chat-ai-icon-btn"
          @click="downloadReport"
          :disabled="!reportContent"
          title="Unduh laporan (.md)"
          aria-label="Unduh laporan"
        >📄</button>
        <button
          class="chat-ai-icon-btn"
          @click="showShortcuts = true"
          title="Pintasan keyboard (?)"
          aria-label="Pintasan keyboard"
        >⌨</button>
        <button
          class="chat-ai-icon-btn chat-ai-new-btn"
          :disabled="!messages.length || isStreaming"
          @click="newSession"
          title="Mulai percakapan baru (Ctrl+N)"
          aria-label="Percakapan baru"
          data-component-id="chat--header--btn-new"
        >
          <svg class="chat-ai-new-svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M12 5v14"></path>
            <path d="M5 12h14"></path>
          </svg>
          <span class="chat-ai-new-label">New Chat</span>
        </button>
      </div>
    </div>

    <!-- ── Tab bar (multi-chat paralel) ─────────────────────────────────────── -->
    <ChatTabBar
      v-if="!isReadOnlyView"
      :tabs="tabViews"
      @select="activateTab"
      @close="closeTab"
      @new="onNewTab"
      @reorder="onReorderTabs"
    />

    <!-- Session list overlay -->
    <ChatSessionList
      v-if="showSessionList"
      :sessions="sessions"
      :is-loading="sessionsLoading"
      :load-error="sessionsLoadError"
      :active-session-id="sessionId"
      :users="adminUsers"
      :active-user-id="viewingUserId"
      :self-user-id="user?.userId || null"
      :is-read-only="isReadOnlyView"
      :read-only-user-name="readOnlyUserName"
      @select="onSelectSession"
      @delete="onDeleteSession"
      @new-session="newSession"
      @refresh="refreshSessionList"
      @close="showSessionList = false"
      @select-user="onSelectAdminUser"
    />

    <!-- Token usage strip -->
    <div
      v-if="cumulativeUsage.input || cumulativeUsage.output"
      class="chat-ai-usage"
      :title="`Total input: ${cumulativeUsage.input} tok · Total output: ${cumulativeUsage.output} tok`"
    >
      <span class="chat-ai-usage-pill"><b>↑</b> {{ formatTokens(cumulativeUsage.input) }}</span>
      <span class="chat-ai-usage-pill"><b>↓</b> {{ formatTokens(cumulativeUsage.output) }}</span>
      <span v-if="lastUsage" class="chat-ai-usage-last">turn: {{ lastUsage.outputTokens }} tok</span>
    </div>

    <!-- ── Messages ──────────────────────────────────────────────────────── -->
    <!-- Read-progress bar (tipis di atas list pesan) -->
    <div
      v-if="messages.length"
      class="chat-ai-progress"
      aria-hidden="true"
    >
      <div class="chat-ai-progress-bar" :style="{ width: scrollProgress + '%' }"></div>
    </div>

    <!-- Floating Q-Navigator -->
    <ChatQNavigator
      :messages="messages"
      :scroll-container="messagesEl"
      @jump="scrollToQ"
      @active-change="onActiveQChange"
    />

    <div ref="messagesEl" class="chat-ai-messages" @scroll="onScroll">
      <!-- Sticky question bar — pin pertanyaan aktif di top saat user
           scroll lewat bubble-nya, jadi konteks "ini jawaban dari pertanyaan
           apa" selalu visible. Click → kembali ke bubble pertanyaan. -->
      <transition name="chat-fade">
        <button
          v-if="stickyQ"
          class="chat-ai-sticky-q"
          type="button"
          :title="`Kembali ke pertanyaan Q${stickyQ.qIndex}`"
          @click="onStickyQClick"
          data-component-id="chat--messages--sticky-q"
        >
          <span class="chat-ai-sticky-q-num">Q{{ stickyQ.qIndex }}</span>
          <span class="chat-ai-sticky-q-text">{{ stickyQ.preview }}</span>
          <span class="chat-ai-sticky-q-arrow" aria-hidden="true">↑</span>
        </button>
      </transition>

      <ChatEmptyState
        v-if="!messages.length"
        :context-label="contextLabel"
        :base-suggestions="baseSuggestions"
        :domain-suggestions="domainSuggestions"
        @select="useSuggestion"
      />

      <transition-group
        name="chat-fade"
        tag="div"
        class="chat-ai-msg-group"
        :class="{ 'chat-ai-msg-group-with-qnav': hasQNav }"
      >
        <ChatMessageBubble
          v-for="msg in messages"
          :key="msg.id"
          :msg="msg"
          :copied="copiedId === msg.id"
          :now-tick="nowTick"
          :q-index="qIndexById.get(msg.id) || null"
          :pulse="pulseMessageId === msg.id"
          @copy="copyMessage"
          @retry="retryLast"
          @fork="onForkMessage"
        />
      </transition-group>

      <ChatToolTimeline
        v-if="toolActivity.length && isStreaming"
        :activity="toolActivity"
        :is-streaming="isStreaming"
        :now-tick="toolTick"
      />

      <div v-if="showFollowUps" class="chat-ai-followups">
        <div class="chat-ai-followups-label">
          <span class="chat-ai-followups-icon">💭</span>
          Lanjutan yang mungkin berguna
        </div>
        <div class="chat-ai-followups-row">
          <button
            v-for="(f, i) in followUps"
            :key="'f-' + i"
            class="chat-ai-followup"
            @click="useSuggestion(f)"
          >
            <span class="chat-ai-followup-text">{{ f }}</span>
            <span class="chat-ai-followup-arrow">→</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Scroll-to-bottom -->
    <transition name="chat-fade">
      <button
        v-if="userScrolledUp && messages.length"
        class="chat-ai-scroll-btn"
        @click="forceScrollToBottom"
        title="Scroll ke bawah"
        aria-label="Scroll ke bawah"
      >
        <span>⬇</span>
        <span v-if="newMessageCount" class="chat-ai-scroll-badge">{{ newMessageCount }}</span>
      </button>
    </transition>

    <!-- Error -->
    <div v-if="error" class="chat-ai-error-bar" role="alert">
      <span class="chat-ai-error-icon">⚠️</span>
      <span class="chat-ai-error-msg">{{ error }}</span>
      <div class="chat-ai-error-actions">
        <button class="chat-ai-error-btn chat-ai-error-btn-retry" @click="retryLast" title="Coba lagi">
          <span>↻</span> Coba lagi
        </button>
        <button class="chat-ai-error-btn" @click="error = null" title="Tutup" aria-label="Tutup">✕</button>
      </div>
    </div>

    <!-- Uploaded MD context -->
    <div v-if="uploadedContext" class="chat-ai-upload-strip">
      <span class="chat-ai-upload-info">
        📎 <b>{{ uploadedContext.name }}</b>
        <span class="chat-ai-upload-size">({{ formatBytes(uploadedContext.size) }})</span>
      </span>
      <button @click="uploadedContext = null" title="Lepas lampiran" aria-label="Lepas lampiran">✕</button>
    </div>

    <!-- Pasted/attached image previews — sent with the next message -->
    <div v-if="attachedImages.length" class="chat-ai-image-strip">
      <div
        v-for="(img, i) in attachedImages"
        :key="img.id"
        class="chat-ai-image-chip"
        :title="`${img.name} · ${formatBytes(img.size)}`"
      >
        <img :src="img.dataUrl" :alt="img.name" />
        <span
          v-if="img.mediaType === 'image/gif'"
          class="chat-ai-image-chip-gif"
          title="GIF dibaca AI sebagai 1 frame statis (bukan animasi)"
        >GIF</span>
        <button
          class="chat-ai-image-chip-x"
          @click="removeImage(i)"
          title="Hapus gambar"
          aria-label="Hapus gambar"
        >✕</button>
      </div>
      <span class="chat-ai-image-strip-hint">
        {{ attachedImages.length }} gambar siap dikirim<template
          v-if="attachedImages.some(i => i.mediaType === 'image/gif')"
        > · GIF dibaca sebagai 1 frame statis</template>
      </span>
    </div>

    <ChatComposer
      ref="composerEl"
      v-model="inputText"
      :is-streaming="isStreaming"
      :is-read-only="isReadOnlyView"
      :read-only-user-name="readOnlyUserName"
      @submit="onSubmit"
      @attach="onAttach"
      @image-paste="onImagePaste"
      @abort="abortStream"
      @escape="onEscapeInput"
    />

    <ChatShortcutsModal :visible="showShortcuts" @close="showShortcuts = false" />
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount } from 'vue'
import { useChatTabs } from '../composables/useChatTabs'
import { useActiveReport } from '../composables/useActiveReport'
import { useWorkflowData } from '../composables/useWorkflowData'
import { useTimeline } from '../composables/useTimeline'
import { useAuth } from '../composables/useAuth'
import {
  useChatSessions, fetchSessionList, fetchSessionDetail, deleteSession, forkSession,
  renameSession, fetchChatUsersForAdmin,
} from '../composables/useChatSessions'
import { useChatNavigation } from '../composables/useChatNavigation'
import { useChatHealth } from '../composables/useChatHealth'
import { downloadChatAsMarkdown, downloadReportMarkdown, readMarkdownFile } from '../utils/chatExport'
import { getBaseSuggestions, getDomainSuggestions } from '../data/chatSuggestions'
import ChatSessionList from './ChatSessionList.vue'
import ChatEmptyState from './ChatEmptyState.vue'
import ChatShortcutsModal from './ChatShortcutsModal.vue'
import ChatMessageBubble from './ChatMessageBubble.vue'
import ChatComposer from './ChatComposer.vue'
import ChatQNavigator from './ChatQNavigator.vue'
import ChatToolTimeline from './ChatToolTimeline.vue'
import ChatTabBar from './ChatTabBar.vue'

const STORAGE_KEY = 'uprint_auth_token'
const CHAT_FONT_STORAGE_KEY = 'uprint_chat_font_scale'
const CHAT_FONT_MIN  = 0.85
const CHAT_FONT_MAX  = 1.50
const CHAT_FONT_STEP = 0.05

function clampChatFont(value) {
  const n = Number(value)
  if (!Number.isFinite(n)) return 1
  return Math.min(CHAT_FONT_MAX, Math.max(CHAT_FONT_MIN, Math.round(n * 20) / 20))
}
function loadInitialChatFont() {
  try {
    const raw = localStorage.getItem(CHAT_FONT_STORAGE_KEY)
    if (raw == null) return 1
    return clampChatFont(parseFloat(raw))
  } catch { return 1 }
}
const chatFontScale   = ref(loadInitialChatFont())
const chatFontPercent = computed(() => Math.round(chatFontScale.value * 100))
function persistChatFont(value) {
  try { localStorage.setItem(CHAT_FONT_STORAGE_KEY, String(value)) } catch {}
}
function setChatFont(value) {
  const next = clampChatFont(value)
  chatFontScale.value = next
  persistChatFont(next)
}
function increaseChatFont() { setChatFont(chatFontScale.value + CHAT_FONT_STEP) }
function decreaseChatFont() { setChatFont(chatFontScale.value - CHAT_FONT_STEP) }
function resetChatFont()    { setChatFont(1) }

// ── Multi-tab: tiap tab = 1 instance chat independen (bisa streaming paralel) ─
// Binding di bawah memetakan "tab aktif" ke nama-nama lama (messages, isStreaming,
// …) supaya seluruh template & logika di file ini tetap bekerja apa adanya;
// hanya sumbernya yang kini berpindah mengikuti tab yang sedang dibuka.
const {
  activeInstance, tabViews, anyBackgroundStreaming,
  newTab, activateTab, closeTab, closeTabBySessionId, openSessionInTab, moveTab,
  restoreTabsForDate, disposeAllLocal, ensureAtLeastOneTab, setPersistEnabled,
} = useChatTabs()

const EMPTY_ARR = []
const messages        = computed(() => activeInstance.value?.messages.value ?? EMPTY_ARR)
const isStreaming     = computed(() => activeInstance.value?.isStreaming.value ?? false)
const sessionId       = computed(() => activeInstance.value?.sessionId.value ?? null)
const sessionTitle    = computed(() => activeInstance.value?.sessionTitle.value ?? null)
const lastUsage       = computed(() => activeInstance.value?.lastUsage.value ?? null)
const cumulativeUsage = computed(() => activeInstance.value?.cumulativeUsage.value ?? { input: 0, output: 0 })
const toolActivity    = computed(() => activeInstance.value?.toolActivity.value ?? EMPTY_ARR)
// error dibaca DAN ditulis (error.value = …) di beberapa tempat → writable computed.
const error = computed({
  get: () => activeInstance.value?.error.value ?? null,
  set: (v) => { if (activeInstance.value) activeInstance.value.error.value = v },
})

function sendMessage(text, opts) { return activeInstance.value?.sendMessage(text, opts) }
function abortStream()           { activeInstance.value?.abortStream() }
function retryLast()             { return activeInstance.value?.retryLast() }
function clearMessages()         { activeInstance.value?.clearMessages() }
function setSession(id, title)   { activeInstance.value?.setSession(id, title) }
const { currentDept, currentReport, isOverview, reportContextKey } = useActiveReport()
const { getReportContent, overviewContent } = useWorkflowData()
const { selectedDate } = useTimeline()
const { user } = useAuth()
const { sessions, isLoading: sessionsLoading, loadError: sessionsLoadError } = useChatSessions()

const inputText        = ref('')
const composerEl       = ref(null)
const messagesEl       = ref(null)
const titleInputEl     = ref(null)
const showSessionList  = ref(false)
const copiedId         = ref(null)
const uploadedContext  = ref(null)
const showShortcuts    = ref(false)
const isEditingTitle   = ref(false)
const titleDraft       = ref('')
const titleSaving      = ref(false)
const newMessageCount  = ref(0)
const nowTick          = ref(Date.now())
const toolTick         = ref(Date.now())
// Images user has pasted/attached and not yet sent. Cleared after each turn.
// Each entry: { id, name, size, mediaType, dataUrl, base64 }.
const attachedImages   = ref([])
const MAX_IMAGE_BYTES  = 5 * 1024 * 1024
const ALLOWED_IMAGE_TYPES = new Set(['image/png', 'image/jpeg', 'image/webp', 'image/gif'])
let _imgIdSeq = 0

// ── Super-admin (Yustian) — view chat history user lain (read-only) ──────
// adminUsers: list user yang punya chat (cuma di-fetch jika user.isUsersAdmin).
// viewingUserId: user yang sedang dilihat (null = lihat sesi sendiri).
// isReadOnlyView: true saat viewingUserId !== self.userId.
const adminUsers      = ref([])
const viewingUserId   = ref(null)
const isReadOnlyView  = computed(() => {
  return !!viewingUserId.value && viewingUserId.value !== user.value?.userId
})
const readOnlyUserName = computed(() => {
  if (!isReadOnlyView.value) return ''
  const match = adminUsers.value.find(u => u.userId === viewingUserId.value)
  return match?.userName || `User ${viewingUserId.value}`
})

// Navigasi pertanyaan (Q1, Q2, …) — di-extract ke composable agar ChatAI.vue
// tetap di bawah hard-limit ukuran file.
const {
  userScrolledUp,
  scrollProgress,
  pulseMessageId,
  qIndexById,
  onScroll,
  scrollToQ,
  jumpQ,
  disposeNavigation,
} = useChatNavigation({ messagesEl, messages, newMessageCount })

const {
  healthChecking, healthStatusClass, healthLabel, healthTooltip, testAiConnection,
} = useChatHealth()

let _nowInterval = null
let _toolInterval = null

// ── Computed ───────────────────────────────────────────────────────────────
// Q-Navigator floating panel mirror logika v-if di ChatQNavigator.vue
// (userQuestions >= 2). Saat aktif, kasih padding-kiri ekstra ke msg-group
// supaya bubble user (yang flex row-reverse) tidak tertindih panel di kiri.
const hasQNav = computed(() => qIndexById.value.size >= 2)
const contextLabel = computed(() => {
  if (isOverview.value) return `${currentDept.value?.label || ''} Overview`
  return currentReport.value?.label || 'Laporan'
})
const sessionDateLabel = computed(() => selectedDate.value || '—')
const reportContent = computed(() => {
  if (isOverview.value) return overviewContent.value || ''
  if (!currentReport.value) return ''
  return getReportContent(currentReport.value.id)?.mdFile || ''
})
const composedContext = computed(() => {
  const parts = []
  if (reportContent.value) parts.push(reportContent.value)
  if (uploadedContext.value?.content) {
    parts.push(`\n\n--- LAMPIRAN: ${uploadedContext.value.name} ---\n${uploadedContext.value.content}`)
  }
  return parts.join('')
})
// Title edit — tombol klik membuka inline input untuk rename judul sesi.
// Title baru langsung di-PATCH ke server dan menjadi judul yang tampil di
// daftar history. Hanya sesi yang sudah punya sessionId yang bisa direname
// (sesi belum dimulai = belum ada record di DB).
const canEditTitle = computed(() => !!sessionId.value)
const titleHoverHint = computed(() => canEditTitle.value
  ? 'Klik untuk mengubah judul percakapan'
  : 'Mulai percakapan dulu untuk memberi judul'
)
const titleEditPlaceholder = computed(() => sessionTitle.value || 'Judul percakapan…')
const titleSizeClass = computed(() => {
  const len = (sessionTitle.value || '').length
  if (len > 80)  return 'title-xs'
  if (len > 50)  return 'title-sm'
  return 'title-md'
})

// Sticky question bar — saat scroll lewat pertanyaan, text-nya pin di top
// chat agar user selalu tahu jawaban yang dibaca itu jawaban dari pertanyaan
// mana. Diisi oleh ChatQNavigator via @active-change.
const activeQ = ref(null) // { qIndex, outOfView } | null
function onActiveQChange(payload) {
  activeQ.value = payload
}
const stickyQ = computed(() => {
  const a = activeQ.value
  if (!a || !a.outOfView) return null
  const target = a.qIndex
  let n = 0
  for (const m of messages.value) {
    if (!m || m.role !== 'user' || m.error || !m.content) continue
    n++
    if (n === target) {
      const text = String(m.content).replace(/\s+/g, ' ').trim()
      return {
        qIndex: target,
        preview: text.length > 140 ? text.slice(0, 137) + '…' : (text || '(kosong)'),
      }
    }
  }
  return null
})
function onStickyQClick() {
  if (stickyQ.value) scrollToQ(stickyQ.value.qIndex)
}

function startTitleEdit() {
  if (!canEditTitle.value || titleSaving.value) return
  titleDraft.value = sessionTitle.value || ''
  isEditingTitle.value = true
  nextTick(() => {
    const el = titleInputEl.value
    if (el) { el.focus(); el.select() }
  })
}
function cancelTitleEdit() {
  isEditingTitle.value = false
  titleDraft.value = ''
}
async function commitTitleEdit() {
  if (!isEditingTitle.value) return
  const next = titleDraft.value.trim().slice(0, 120)
  isEditingTitle.value = false
  if (!sessionId.value) return
  if (!next || next === (sessionTitle.value || '')) {
    titleDraft.value = ''
    return
  }
  titleSaving.value = true
  try {
    const ok = await renameSession(sessionId.value, next)
    if (ok) {
      setSession(sessionId.value, next)
      refreshSessionList()
    } else {
      error.value = 'Gagal menyimpan judul percakapan.'
    }
  } finally {
    titleSaving.value = false
    titleDraft.value = ''
  }
}

const baseSuggestions   = computed(() => getBaseSuggestions(isOverview.value))
const domainSuggestions = computed(() => getDomainSuggestions(currentDept.value?.id, currentReport.value?.id, isOverview.value))
// Follow-ups kontekstual: HANYA tampilkan saran yang AI generate sendiri untuk
// turn terakhir (lewat blok <follow-ups> di system prompt). TIDAK ADA fallback
// ke template generik — kalau AI tidak menghasilkan saran kontekstual, lebih
// baik kosong daripada menampilkan kalimat template yang tidak nyambung.
const followUps = computed(() => {
  const last = messages.value[messages.value.length - 1]
  if (last && Array.isArray(last.followUps) && last.followUps.length) {
    return last.followUps
  }
  return []
})
const showFollowUps = computed(() => {
  if (isStreaming.value) return false
  const last = messages.value[messages.value.length - 1]
  if (!last || last.role !== 'assistant' || !last.content || last.error) return false
  return Array.isArray(last.followUps) && last.followUps.length > 0
})
// Tick cepat hanya saat ada tool yang sedang berjalan (belum ada endedAt) dan
// streaming masih aktif. Saat idle, interval dimatikan agar tidak konsumsi CPU.
const toolRunning = computed(() => {
  if (!isStreaming.value) return false
  // Cek SEMUA tool, bukan cuma yang terakhir — saat AI memanggil tool paralel,
  // item terakhir bisa selesai duluan sementara yang lain masih jalan. Kalau
  // hanya cek item terakhir, interval mati & elapsed tool lain membeku.
  return toolActivity.value.some(t => t.startedAt && !t.endedAt)
})
watch(toolRunning, (running) => {
  if (running) {
    if (_toolInterval) return
    toolTick.value = Date.now()
    _toolInterval = setInterval(() => { toolTick.value = Date.now() }, 250)
  } else {
    if (_toolInterval) { clearInterval(_toolInterval); _toolInterval = null }
    toolTick.value = Date.now()
  }
})

// ── Helpers ────────────────────────────────────────────────────────────────
function formatTokens(n) {
  if (!n) return '0'
  if (n < 1000) return String(n)
  if (n < 1_000_000) return (n / 1000).toFixed(1) + 'k'
  return (n / 1_000_000).toFixed(2) + 'M'
}
function formatBytes(n) {
  if (n < 1024) return n + 'B'
  if (n < 1024 * 1024) return (n / 1024).toFixed(1) + 'KB'
  return (n / 1024 / 1024).toFixed(1) + 'MB'
}
function getStoredToken() {
  try { return localStorage.getItem(STORAGE_KEY) || '' } catch { return '' }
}

// ── Actions ────────────────────────────────────────────────────────────────
function useSuggestion(text) {
  inputText.value = text
  nextTick(() => composerEl.value?.focus())
}

async function onSubmit() {
  // Read-only mode (super-admin sedang lihat user lain) — composer di-disable
  // di UI, tapi guard di sini supaya keystroke programatis (paste→Enter dari
  // shortcut) tidak bypass.
  if (isReadOnlyView.value) return
  const text = inputText.value.trim()
  // Allow image-only sends so user can paste a screenshot tanpa caption.
  if ((!text && !attachedImages.value.length) || isStreaming.value) return
  inputText.value = ''
  userScrolledUp.value = false
  newMessageCount.value = 0
  // Snapshot + clear so the next turn starts fresh, even if send is async.
  const imgs = attachedImages.value.slice()
  attachedImages.value = []
  await sendMessage(text || '(gambar terlampir)', {
    context:     composedContext.value,
    department:  currentDept.value?.id || null,
    reportId:    isOverview.value ? null : currentReport.value?.id || null,
    sessionDate: selectedDate.value || null,
    token:       getStoredToken(),
    images:      imgs.map(i => ({
      name:      i.name,
      mediaType: i.mediaType,
      base64:    i.base64,
      dataUrl:   i.dataUrl,
    })),
  })
  refreshSessionList()
}

function onEscapeInput() {
  if (isStreaming.value) abortStream()
  else if (showSessionList.value) showSessionList.value = false
}

async function onAttach(file) {
  try { uploadedContext.value = await readMarkdownFile(file) }
  catch (err) { error.value = err.message }
}

async function onImagePaste(file) {
  if (!file) return
  if (!ALLOWED_IMAGE_TYPES.has(file.type)) {
    error.value = `Tipe gambar ${file.type || '(tidak dikenal)'} belum didukung. Gunakan PNG/JPEG/WebP/GIF.`
    return
  }
  if (file.size > MAX_IMAGE_BYTES) {
    error.value = `Gambar terlalu besar (${formatBytes(file.size)}). Batas maksimal ${formatBytes(MAX_IMAGE_BYTES)}.`
    return
  }
  try {
    const dataUrl = await readFileAsDataUrl(file)
    const base64 = dataUrl.includes(',') ? dataUrl.split(',', 2)[1] : ''
    if (!base64) throw new Error('Gagal membaca gambar.')
    _imgIdSeq += 1
    attachedImages.value.push({
      id:        `img_${Date.now()}_${_imgIdSeq}`,
      name:      file.name || `pasted-${_imgIdSeq}.png`,
      size:      file.size,
      mediaType: file.type,
      dataUrl,
      base64,
    })
  } catch (err) {
    error.value = err?.message || 'Gagal memproses gambar.'
  }
}

function removeImage(idx) {
  if (idx < 0 || idx >= attachedImages.value.length) return
  attachedImages.value.splice(idx, 1)
}

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload  = () => resolve(String(reader.result || ''))
    reader.onerror = () => reject(reader.error || new Error('Read error'))
    reader.readAsDataURL(file)
  })
}

async function refreshSessionList() {
  // Persistensi chat dipatok ke (user, sessionDate). Sidebar history menampilkan
  // SEMUA sesi user lintas tanggal & lintas departemen — jadi tidak ada filter.
  // Saat super-admin sedang melihat user lain (viewingUserId set), pass asUserId
  // agar server mengembalikan sesi user tersebut, bukan sesi self.
  const asUserId = isReadOnlyView.value ? viewingUserId.value : null
  await fetchSessionList({ limit: 50, asUserId })
}

// Super-admin pilih user dari carousel tab — re-fetch session list untuk
// user tsb dan clear chat aktif (karena loadSession akan menggantinya).
async function onSelectAdminUser(u) {
  if (!u?.userId) return
  const isSelf = u.userId === user.value?.userId
  viewingUserId.value = isSelf ? null : u.userId
  // Pindah view: reset tab. Saat melihat user lain (read-only) matikan persist
  // agar tab sesi mereka tidak ikut ditulis ke state tab milik kita.
  setPersistEnabled(!isReadOnlyView.value)
  if (isSelf) {
    await loadTabsForDate()
  } else {
    disposeAllLocal()
    ensureAtLeastOneTab()
    await refreshSessionList()
  }
}

async function loadAdminUsers() {
  if (!user.value?.isUsersAdmin) {
    adminUsers.value = []
    return
  }
  adminUsers.value = await fetchChatUsersForAdmin()
}

// Muat tab-tab terbuka untuk tanggal aktif. Tab tersimpan di DB (IsOpenTab),
// jadi setelah reload tab muncul lagi persis seperti sebelumnya.
async function loadTabsForDate() {
  if (!user.value) return
  // Read-only (super-admin lihat user lain): tab tidak berlaku — cukup
  // refresh daftar sesi, satu tab kosong sebagai kanvas untuk membuka sesi.
  if (isReadOnlyView.value) {
    disposeAllLocal()
    ensureAtLeastOneTab()
    await refreshSessionList()
    return
  }
  await refreshSessionList()
  await restoreTabsForDate(selectedDate.value || null)
  // Fallback kompat: belum ada tab tersimpan (mis. sesi lama pra-fitur-tab).
  // Buka sesi terbaru tanggal ini ke tab pertama supaya UX lama tetap terasa.
  const views = tabViews.value
  if (views.length === 1 && views[0].empty) {
    const target = selectedDate.value
      ? sessions.value.find(s => s.sessionDate === selectedDate.value)
      : sessions.value[0]
    if (target?.sessionId) {
      const detail = await fetchSessionDetail(target.sessionId)
      if (detail) openSessionInTab(detail)
    }
  }
}

function toggleSessionList() {
  showSessionList.value = !showSessionList.value
  if (showSessionList.value) refreshSessionList()
}

async function onSelectSession(s) {
  if (!s?.sessionId) return
  showSessionList.value = false
  const detail = await fetchSessionDetail(s.sessionId)
  // Buka sesi ke dalam tab — kalau sudah terbuka, cukup aktifkan tab-nya.
  if (detail) openSessionInTab(detail)
}

async function onDeleteSession(s) {
  // Read-only: super-admin TIDAK boleh menghapus sesi user lain.
  if (isReadOnlyView.value) return
  const ok = await deleteSession(s.sessionId)
  // Kalau sesi yang dihapus sedang terbuka di sebuah tab, tutup tab itu.
  if (ok) closeTabBySessionId(s.sessionId)
}

function onNewTab() {
  newTab()
  showSessionList.value = false
  nextTick(() => composerEl.value?.focus())
}

function onReorderTabs({ fromId, toId }) {
  const toIndex = tabViews.value.findIndex(t => t.id === toId)
  if (toIndex !== -1) moveTab(fromId, toIndex)
}

// Fork dari pesan tertentu — buat sesi baru berisi pesan-pesan sampai titik ini,
// lalu pindahkan view ke sesi baru tersebut. User bisa langsung lanjut chat
// di cabang baru tanpa mengganggu sesi sumber.
async function onForkMessage(msg) {
  if (!msg?.messageId || !sessionId.value || isStreaming.value) return
  const forked = await forkSession(sessionId.value, { fromMessageId: msg.messageId })
  if (!forked) {
    error.value = 'Gagal mencabangkan percakapan. Coba lagi.'
    return
  }
  // Cabang selalu dibuka di tab baru (sesi sumber masih di tab sebelumnya).
  openSessionInTab(forked)
  await refreshSessionList()
  showSessionList.value = false
  nextTick(() => composerEl.value?.focus())
}

function newSession() {
  // Tombol "New Chat" di header = buka TAB baru kosong.
  // Klik saat read-only = keluar dari mode read-only & kembali ke chat sendiri.
  if (isReadOnlyView.value) {
    viewingUserId.value = null
    setPersistEnabled(true)
    loadTabsForDate()
    showSessionList.value = false
    return
  }
  newTab()
  showSessionList.value = false
  nextTick(() => composerEl.value?.focus())
}

async function copyMessage(msg) {
  try {
    await navigator.clipboard.writeText(msg.content || '')
    copiedId.value = msg.id
    setTimeout(() => { if (copiedId.value === msg.id) copiedId.value = null }, 1500)
  } catch { /* ignore */ }
}

function downloadChat() {
  if (!messages.value.length) return
  downloadChatAsMarkdown({
    messages: messages.value,
    title: sessionTitle.value || contextLabel.value,
    sessionDate: selectedDate.value,
    department: currentDept.value?.label,
    reportLabel: contextLabel.value,
  })
}

function downloadReport() {
  if (!reportContent.value) return
  downloadReportMarkdown({ title: contextLabel.value, content: reportContent.value })
}

function scrollToBottom() {
  const el = messagesEl.value
  if (!el) return
  el.scrollTop = el.scrollHeight
}
function forceScrollToBottom() {
  userScrolledUp.value = false
  newMessageCount.value = 0
  nextTick(scrollToBottom)
}

// ── Global keyboard ───────────────────────────────────────────────────────
function onGlobalKeydown(e) {
  // ignore when typing in input/textarea except our own intercepts
  const tag = (e.target?.tagName || '').toLowerCase()
  const isFormField = tag === 'input' || tag === 'textarea' || e.target?.isContentEditable
  if (e.key === 'Escape') {
    if (showShortcuts.value) { showShortcuts.value = false; return }
    if (showSessionList.value) { showSessionList.value = false; return }
  }
  if (isFormField) return
  if (e.key === '?' && !e.ctrlKey && !e.metaKey) {
    e.preventDefault(); showShortcuts.value = !showShortcuts.value; return
  }
  if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
    e.preventDefault(); toggleSessionList(); return
  }
  if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'n') {
    e.preventDefault(); if (messages.value.length && !isStreaming.value) newSession(); return
  }
  if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'd') {
    if (messages.value.length) { e.preventDefault(); downloadChat() }
    return
  }
  // Navigasi pertanyaan: [ untuk Q sebelumnya, ] untuk Q berikutnya.
  if (!e.ctrlKey && !e.metaKey && !e.altKey) {
    if (e.key === '[') { e.preventDefault(); jumpQ(-1); return }
    if (e.key === ']') { e.preventDefault(); jumpQ(1);  return }
  }
}

// ── Watchers ───────────────────────────────────────────────────────────────
watch(messages, () => {
  if (userScrolledUp.value) {
    const last = messages.value[messages.value.length - 1]
    if (last?.role === 'assistant' && !last.streaming) newMessageCount.value++
  } else {
    nextTick(scrollToBottom)
  }
}, { deep: true })

watch(() => messages.value.length, (len) => {
  if (len === 0) { userScrolledUp.value = false; newMessageCount.value = 0 }
})

// Auto-scroll saat tool timeline berkembang. toolActivity TIDAK ada di array
// `messages`, jadi watcher messages tidak ter-trigger saat tool baru di-push.
// Tanpa ini, tool timeline yang muncul di bawah bubble assistant bisa keluar
// dari viewport scroll (kotak ke-2/3 terpotong di bagian bawah).
watch(() => toolActivity.value.length, (newLen, oldLen) => {
  if (newLen > (oldLen || 0) && !userScrolledUp.value) {
    nextTick(scrollToBottom)
  }
})

// Pindah report/department TIDAK reset chat. Persistensi chat dipatok ke
// (user, sessionDate) — bukan per departemen/sub-laporan. Konteks laporan
// baru otomatis ikut di prompt selanjutnya via `composedContext` reaktif.
// Hanya bersihkan attachment lokal yang spesifik report aktif.
watch(reportContextKey, () => {
  uploadedContext.value = null
})

// Pindah tanggal di timeline = ganti sesi. Clear chat aktif lalu auto-load
// sesi tanggal baru (atau tetap kosong jika belum ada — pesan pertama akan
// membuat sesi baru untuk tanggal itu).
watch(selectedDate, () => {
  // Ganti tanggal = himpunan tab yang berbeda. Restore tab tanggal baru.
  uploadedContext.value = null
  loadTabsForDate()
})

onMounted(() => {
  nextTick(() => composerEl.value?.focus())
  if (user.value) {
    setPersistEnabled(!isReadOnlyView.value)
    loadTabsForDate()
    loadAdminUsers()
  } else {
    // Belum login — tetap sediakan satu tab kosong sebagai kanvas.
    ensureAtLeastOneTab()
  }
  testAiConnection()
  window.addEventListener('keydown', onGlobalKeydown)
  _nowInterval = setInterval(() => { nowTick.value = Date.now() }, 30000)
})

// Saat super-admin baru selesai validasi token (user.value muncul belakangan),
// fetch list user untuk carousel. autoLoadActiveSession sudah handle re-load
// chat sendiri di flow `validateFromUrl` → user.value berubah dari null.
watch(() => user.value?.userId, (uid, prev) => {
  if (!uid) return
  loadAdminUsers()
  // User baru muncul (mis. super-admin validasi token via URL) → muat tab-nya.
  if (!prev) {
    setPersistEnabled(!isReadOnlyView.value)
    loadTabsForDate()
  }
})

onBeforeUnmount(() => {
  window.removeEventListener('keydown', onGlobalKeydown)
  if (_nowInterval) clearInterval(_nowInterval)
  if (_toolInterval) clearInterval(_toolInterval)
  disposeNavigation()
})
</script>

<style scoped>
.chat-ai {
  position: relative; display: flex; flex-direction: column;
  height: 100%; width: 100%;
  background: var(--bg-base, var(--bg-deep));
  color: var(--text-base, var(--text-body));
  font-size: 13px; overflow: hidden;
}

/* ── Header ─────────────────────────────────────────────────────────────── */
.chat-ai-header {
  display: flex; align-items: flex-start; gap: 6px;
  padding: 9px 11px; flex-shrink: 0; z-index: 2;
  border-bottom: 1px solid var(--border-soft, var(--border-light));
  background: linear-gradient(180deg, var(--bg-elev, var(--bg-card)), var(--bg-base, var(--bg-deep)));
}
.chat-ai-history-btn {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 4px 9px;
  color: var(--text-base, var(--text-body));
  align-self: flex-start;
}
.chat-ai-history-btn:hover:not(:disabled) {
  color: var(--accent, var(--accent-blue, #3b82f6));
  border-color: var(--accent, var(--accent-blue, #3b82f6));
}
.chat-ai-history-btn.is-active {
  background: color-mix(in srgb, var(--accent, #3b82f6) 14%, transparent);
  border-color: var(--accent, var(--accent-blue, #3b82f6));
  color: var(--accent, var(--accent-blue, #3b82f6));
}
.chat-ai-history-svg {
  flex-shrink: 0; width: 15px; height: 15px;
  stroke: currentColor;
}
.chat-ai-history-label {
  font-size: 11px; font-weight: 500; letter-spacing: 0.1px;
}
.chat-ai-header-info {
  display: flex; flex-direction: column; gap: 2px;
  flex: 1; min-width: 0;
}
.chat-ai-title-row {
  display: flex; align-items: flex-start; gap: 6px; min-width: 0;
}
.chat-ai-title-dot {
  width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0;
  margin-top: 6px;
  background: var(--accent, var(--accent-blue, #3b82f6));
  box-shadow: 0 0 6px var(--accent, var(--accent-blue, #3b82f6));
  animation: chat-ai-glow 2.4s ease-in-out infinite;
}
.chat-ai-title-btn {
  flex: 1; min-width: 0;
  display: inline-flex; align-items: flex-start; gap: 6px;
  padding: 2px 6px; margin: -2px -6px;
  background: transparent; border: 1px solid transparent; border-radius: 5px;
  text-align: left; cursor: pointer;
  font-weight: 600; letter-spacing: -0.1px;
  color: var(--text-primary, var(--text-body));
  transition: background 0.12s, border-color 0.12s, color 0.12s;
}
.chat-ai-title-btn:hover:not(:disabled) {
  background: var(--bg-hover, rgba(255, 255, 255, 0.04));
  border-color: var(--border-soft, var(--border-light));
}
.chat-ai-title-btn:disabled { cursor: default; opacity: 0.85; }
.chat-ai-title-btn.title-md { font-size: 13px; line-height: 1.35; }
.chat-ai-title-btn.title-sm { font-size: 12px; line-height: 1.35; }
.chat-ai-title-btn.title-xs { font-size: 11.5px; line-height: 1.3; }
.chat-ai-title-text {
  flex: 1; min-width: 0;
  overflow-wrap: anywhere; word-break: break-word;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;
  overflow: hidden;
}
.chat-ai-title-edit-hint {
  flex-shrink: 0; opacity: 0; font-size: 11px; line-height: 1.4;
  color: var(--text-muted, var(--text-ghost, #888));
  transition: opacity 0.12s;
}
.chat-ai-title-btn:hover:not(:disabled) .chat-ai-title-edit-hint { opacity: 1; }
.chat-ai-title-input {
  flex: 1; min-width: 0;
  padding: 2px 6px; margin: -2px -6px;
  background: var(--bg-elev, var(--bg-card));
  border: 1px solid var(--accent, var(--accent-blue, #3b82f6));
  border-radius: 5px;
  color: var(--text-primary, var(--text-body));
  font-weight: 600; letter-spacing: -0.1px;
  outline: none;
}
.chat-ai-title-input.title-md { font-size: 13px; line-height: 1.35; }
.chat-ai-title-input.title-sm { font-size: 12px; line-height: 1.35; }
.chat-ai-title-input.title-xs { font-size: 11.5px; line-height: 1.3; }
@keyframes chat-ai-glow {
  0%, 100% { opacity: 0.7; }
  50%      { opacity: 1; }
}
.chat-ai-context-label {
  font-size: 10.5px; color: var(--text-muted, var(--text-ghost, #888));
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.chat-ai-header-actions { display: flex; gap: 3px; align-self: flex-start; }

/* ── Font-size group ─ A− | xx% | A+ ─ scaling readability percakapan AI ─ */
.chat-ai-fontsize-group {
  display: inline-flex; align-items: stretch; gap: 0;
  border-radius: 6px; overflow: hidden;
  border: 1px solid var(--border-soft, var(--border-light));
  margin-right: 1px;
}
.chat-ai-fontsize-group .chat-ai-icon-btn {
  border: none; border-radius: 0;
  padding: 5px 7px; font-size: 11px; font-weight: 600;
  letter-spacing: 0.2px;
}
.chat-ai-fontsize-group .chat-ai-icon-btn + .chat-ai-icon-btn {
  border-left: 1px solid var(--border-soft, var(--border-light));
}
.chat-ai-fontsize-label {
  min-width: 38px; text-align: center;
  font-variant-numeric: tabular-nums;
  color: var(--text-muted, var(--text-ghost, #888));
}
.chat-ai-fontsize-label:hover:not(:disabled) {
  color: var(--accent, var(--accent-blue, #3b82f6));
}

.chat-ai-icon-btn {
  background: transparent; cursor: pointer;
  border: 1px solid var(--border-soft, var(--border-light));
  color: var(--text-base, var(--text-body));
  padding: 5px 8px; border-radius: 5px;
  font-size: 12px; line-height: 1;
  transition: background 0.12s, border-color 0.12s, transform 0.08s, color 0.12s;
}
.chat-ai-icon-btn:hover:not(:disabled) {
  background: var(--bg-hover, var(--bg-hover));
  border-color: var(--border-accent, var(--border-soft));
}
.chat-ai-icon-btn:active:not(:disabled) { transform: scale(0.93); }
.chat-ai-icon-btn:disabled { opacity: 0.35; cursor: not-allowed; }
.chat-ai-new-btn {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 4px 9px;
  font-weight: 500;
}
.chat-ai-new-btn:hover:not(:disabled) {
  border-color: var(--accent, var(--accent-blue, #3b82f6));
  color: var(--accent-blue-light, var(--accent, #3b82f6));
}
.chat-ai-new-svg { flex-shrink: 0; width: 13px; height: 13px; stroke: currentColor; }
.chat-ai-new-label { font-size: 11px; letter-spacing: 0.1px; line-height: 1; white-space: nowrap; }

/* Health dot — compact, label removed (status communicated by color + tooltip) */
.chat-ai-health-btn {
  display: inline-flex; align-items: center; justify-content: center;
  width: 26px; height: 26px; padding: 0;
}
.chat-ai-health-btn:disabled { opacity: 0.7; }
.chat-ai-health-dot {
  width: 9px; height: 9px; border-radius: 50%; flex-shrink: 0;
  background: var(--text-dim, #888);
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.25);
  transition: background 0.2s, box-shadow 0.2s;
}
.chat-ai-health-dot.health-ok       { background: var(--accent-green,  #22c55e); box-shadow: 0 0 8px rgba(34, 197, 94, 0.75); }
.chat-ai-health-dot.health-fail     { background: var(--accent-red,    #ef4444); box-shadow: 0 0 8px rgba(239, 68, 68, 0.75); }
.chat-ai-health-dot.health-checking { background: var(--accent-yellow, #eab308); box-shadow: 0 0 8px rgba(234, 179, 8, 0.75); animation: chat-ai-health-pulse 1s infinite ease-in-out; }
.chat-ai-health-dot.health-unknown  { background: var(--text-dim,      #888); }
@keyframes chat-ai-health-pulse {
  0%, 100% { opacity: 1; }
  50%      { opacity: 0.4; }
}

/* ── Token usage strip ─────────────────────────────────────────────────── */
.chat-ai-usage {
  display: flex; align-items: center; gap: 6px; flex-shrink: 0;
  padding: 4px 10px; font-size: 10.5px;
  color: var(--text-muted, var(--text-ghost));
  background: var(--bg-elev, var(--bg-card));
  border-bottom: 1px solid var(--border-soft, var(--border-light));
}
.chat-ai-usage-pill {
  display: inline-flex; align-items: center; gap: 3px;
  padding: 1px 6px; border-radius: 9px;
  background: var(--bg-base, var(--bg-deep));
  border: 1px solid var(--border-soft, var(--border-light));
  font-variant-numeric: tabular-nums;
}
.chat-ai-usage-pill b { color: var(--text-base, var(--text-body)); font-weight: 700; }
.chat-ai-usage-last { margin-left: auto; opacity: 0.75; font-variant-numeric: tabular-nums; }

/* ── Read-progress bar (tipis di atas list) ─────────────────────────────── */
.chat-ai-progress {
  position: relative;
  height: 2px;
  background: transparent;
  flex-shrink: 0;
  z-index: 2;
}
.chat-ai-progress-bar {
  height: 100%;
  background: linear-gradient(
    90deg,
    var(--accent, var(--accent-blue, #3b82f6)),
    var(--accent-purple, #8b5cf6)
  );
  border-radius: 0 2px 2px 0;
  transition: width 0.15s ease-out;
  box-shadow: 0 0 6px rgba(59, 130, 246, 0.45);
}

/* ── Messages ──────────────────────────────────────────────────────────── */
.chat-ai-messages {
  flex: 1 1 auto; overflow-y: auto;
  display: flex; flex-direction: column; gap: 12px;
  padding: 14px 14px 18px;
  scroll-behavior: smooth;
}
.chat-ai-msg-group { display: flex; flex-direction: column; gap: 14px; }
/* Q-Navigator floats absolute di kiri (left:6px width:44px + padding/border ≈ 56px footprint).
   Saat panel aktif, sisakan ruang kiri ~58px supaya bubble user tidak tertindih panel. */
.chat-ai-msg-group-with-qnav { padding-left: 58px; }

/* Sticky question bar — pin pertanyaan aktif ke top scroll area saat user
   sudah scroll lewat bubble-nya. Membuat user selalu sadar "ini jawaban
   dari pertanyaan apa" tanpa harus scroll balik. */
.chat-ai-sticky-q {
  position: sticky; top: 0; z-index: 5;
  display: flex; align-items: center; gap: 8px;
  /* Negative margin extends the bar to the full width of .chat-ai-messages,
     ignoring its 14px horizontal padding so the sticky bar feels edge-to-edge. */
  margin: -14px -14px 0;
  padding: 7px 12px;
  border: none;
  border-bottom: 1px solid var(--border-soft, var(--border-light));
  background: linear-gradient(
    180deg,
    color-mix(in srgb, var(--accent, #3b82f6) 14%, var(--bg-elev, var(--bg-card))),
    var(--bg-elev, var(--bg-card))
  );
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  cursor: pointer;
  text-align: left;
  color: var(--text-base, var(--text-body));
  font: inherit;
  box-shadow: 0 6px 16px -8px rgba(0, 0, 0, 0.45);
  transition: background 0.15s ease, transform 0.08s ease;
}
.chat-ai-sticky-q:hover {
  background: linear-gradient(
    180deg,
    color-mix(in srgb, var(--accent, #3b82f6) 22%, var(--bg-elev, var(--bg-card))),
    var(--bg-elev, var(--bg-card))
  );
}
.chat-ai-sticky-q:active { transform: translateY(1px); }
.chat-ai-sticky-q-num {
  flex-shrink: 0;
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 26px; height: 18px;
  padding: 0 6px;
  border-radius: 5px;
  background: var(--accent, var(--accent-blue, #3b82f6));
  color: #fff;
  font-size: 10px; font-weight: 700; letter-spacing: 0.4px;
  line-height: 1;
}
.chat-ai-sticky-q-text {
  flex: 1; min-width: 0;
  font-size: 12px; font-weight: 500; line-height: 1.35;
  color: var(--text-primary, var(--text-base));
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.chat-ai-sticky-q-arrow {
  flex-shrink: 0;
  font-size: 12px; line-height: 1;
  color: var(--text-muted, var(--text-ghost));
  opacity: 0.7;
}
.chat-ai-sticky-q:hover .chat-ai-sticky-q-arrow {
  opacity: 1;
  color: var(--accent, var(--accent-blue, #3b82f6));
}

.chat-fade-enter-active, .chat-fade-leave-active { transition: all 0.25s ease; }
.chat-fade-enter-from { opacity: 0; transform: translateY(8px); }
.chat-fade-leave-to   { opacity: 0; transform: translateY(-4px); }

/* Follow-ups */
.chat-ai-followups {
  display: flex; flex-direction: column; gap: 6px;
  padding: 4px 0 4px 39px;
  animation: chat-fade-in 0.3s ease;
}
@keyframes chat-fade-in {
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: translateY(0); }
}
.chat-ai-followups-label {
  display: inline-flex; align-items: center; gap: 5px;
  font-size: 10.5px; font-weight: 600;
  color: var(--text-muted, var(--text-ghost, #888));
  text-transform: uppercase; letter-spacing: 0.5px;
}
.chat-ai-followups-icon { font-size: 11px; }
.chat-ai-followups-row { display: flex; flex-wrap: wrap; gap: 5px; }
.chat-ai-followup {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 5px 10px; border-radius: 16px; cursor: pointer;
  font-size: 11.5px; font-family: inherit; text-align: left;
  background: transparent;
  border: 1px dashed var(--border-soft, var(--border-light));
  color: var(--text-base, var(--text-body));
  transition: background 0.15s, border-color 0.15s, color 0.15s;
}
.chat-ai-followup:hover {
  background: var(--accent-soft, rgba(59, 130, 246, 0.08));
  border-color: var(--accent, var(--accent-blue, #3b82f6));
  border-style: solid;
}
.chat-ai-followup-arrow {
  color: var(--text-dim, #6b7a8d);
  transition: color 0.15s, transform 0.15s;
}
.chat-ai-followup:hover .chat-ai-followup-arrow {
  color: var(--accent, var(--accent-blue, #3b82f6));
  transform: translateX(2px);
}

/* Scroll-to-bottom */
.chat-ai-scroll-btn {
  position: absolute; right: 16px; bottom: 92px; z-index: 4;
  width: 36px; height: 36px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  background: var(--accent, var(--accent-blue, #3b82f6));
  color: white; border: none; cursor: pointer; font-size: 14px;
  box-shadow: 0 4px 14px -2px rgba(0, 0, 0, 0.45), 0 0 0 1px rgba(255,255,255,0.06);
  transition: transform 0.15s, box-shadow 0.15s;
}
.chat-ai-scroll-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px -4px rgba(0, 0, 0, 0.55);
}
.chat-ai-scroll-badge {
  position: absolute; top: -4px; right: -4px;
  min-width: 18px; height: 18px; padding: 0 4px;
  display: flex; align-items: center; justify-content: center;
  border-radius: 9px;
  background: var(--accent-red, #ef4444);
  color: #fff; font-size: 10px; font-weight: 700;
  border: 2px solid var(--bg-base, var(--bg-deep));
}

/* Error bar */
.chat-ai-error-bar {
  display: flex; align-items: center; gap: 8px; flex-shrink: 0;
  padding: 8px 12px; font-size: 12px;
  background: rgba(239, 68, 68, 0.1);
  border-top: 1px solid rgba(239, 68, 68, 0.4);
  color: var(--accent-red-light, #f87171);
  animation: chat-fade-in 0.2s ease;
}
.chat-ai-error-icon { font-size: 14px; }
.chat-ai-error-msg { flex: 1; }
.chat-ai-error-actions { display: flex; gap: 6px; }
.chat-ai-error-btn {
  background: transparent; cursor: pointer;
  border: 1px solid rgba(239, 68, 68, 0.45);
  color: var(--accent-red-light, #f87171);
  font-size: 11px; font-family: inherit;
  padding: 3px 8px; border-radius: 4px;
  transition: background 0.12s;
}
.chat-ai-error-btn:hover { background: rgba(239, 68, 68, 0.18); }
.chat-ai-error-btn-retry { font-weight: 600; }

/* Upload strip */
.chat-ai-upload-strip {
  display: flex; align-items: center; justify-content: space-between;
  flex-shrink: 0; padding: 6px 12px; font-size: 11.5px;
  background: var(--accent-soft, rgba(59, 130, 246, 0.1));
  border-top: 1px solid var(--accent, var(--accent-blue, #3b82f6));
  color: var(--text-base, var(--text-body));
}
.chat-ai-upload-info { display: inline-flex; align-items: center; gap: 4px; }
.chat-ai-upload-size { color: var(--text-dim, #888); margin-left: 2px; }
.chat-ai-upload-strip button {
  background: transparent; border: none; cursor: pointer;
  color: inherit; font-size: 12px;
  padding: 2px 6px; border-radius: 3px;
}
.chat-ai-upload-strip button:hover { background: rgba(255,255,255,0.06); }

/* Pasted-image preview strip — chips with thumbnails */
.chat-ai-image-strip {
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
  flex-shrink: 0; padding: 8px 12px;
  background: var(--bg-elev, var(--bg-card));
  border-top: 1px solid var(--border-soft, var(--border-light));
}
.chat-ai-image-chip {
  position: relative; width: 56px; height: 56px;
  border-radius: 6px; overflow: hidden; flex-shrink: 0;
  border: 1px solid var(--border-soft, var(--border-light));
  background: var(--bg-base, var(--bg-deep));
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
}
.chat-ai-image-chip img {
  width: 100%; height: 100%; object-fit: cover; display: block;
}
.chat-ai-image-chip-x {
  position: absolute; top: 2px; right: 2px;
  width: 18px; height: 18px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  background: rgba(0, 0, 0, 0.65); color: #fff;
  border: none; cursor: pointer; font-size: 10px;
  transition: background 0.12s;
}
.chat-ai-image-chip-x:hover { background: var(--accent-red, #ef4444); }
.chat-ai-image-chip-gif {
  position: absolute; left: 2px; bottom: 2px;
  background: rgba(0, 0, 0, 0.72); color: #fff;
  font-size: 8px; font-weight: 700; letter-spacing: 0.5px;
  padding: 1px 4px; border-radius: 4px;
  pointer-events: none;
}
.chat-ai-image-strip-hint {
  font-size: 11px; color: var(--text-dim, #6b7a8d);
  margin-left: auto;
}

@media (max-width: 768px) {
  .chat-ai-header {
    padding: 8px 10px;
    gap: 4px;
    flex-wrap: wrap;
  }
  .chat-ai-header-info {
    min-width: 0;
    order: 3;
    width: 100%;
  }
  .chat-ai-title-btn,
  .chat-ai-title-input {
    font-size: 12px !important;
  }
  .chat-ai-context-label {
    font-size: 10px;
  }
  .chat-ai-header-actions {
    order: 2;
    margin-left: auto;
  }
  .chat-ai-icon-btn {
    padding: 6px 7px;
    min-width: 32px;
    min-height: 32px;
  }
  .chat-ai-history-label,
  .chat-ai-new-label {
    display: none;
  }
  .chat-ai-fontsize-group {
    display: none;
  }
  .chat-ai-messages {
    padding: 10px 10px 14px;
    gap: 10px;
  }
  .chat-ai-msg-group {
    gap: 12px;
  }
  .chat-ai-msg-group-with-qnav {
    padding-left: 0;
  }
  .chat-ai-sticky-q {
    margin: -10px -10px 0;
    padding: 6px 10px;
  }
  .chat-ai-followups {
    padding-left: 0;
  }
  .chat-ai-followups-row {
    flex-direction: column;
  }
  .chat-ai-followup {
    width: 100%;
    padding: 8px 12px;
    font-size: 12px;
  }
  .chat-ai-scroll-btn {
    bottom: 100px;
    right: 12px;
    width: 40px;
    height: 40px;
  }
  .chat-ai-error-bar {
    flex-wrap: wrap;
    gap: 6px;
    padding: 10px 12px;
  }
}

@media (max-width: 480px) {
  .chat-ai-header {
    padding: 6px 8px;
  }
  .chat-ai-messages {
    padding: 8px 8px 12px;
  }
}
</style>
